import { useEffect, useState, useRef } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useRole,
  useClick,
  useInteractions,
  FloatingFocusManager,
  arrow,
  useId,
  useHover,
  useFocus,
  FloatingPortal
} from '@floating-ui/react';
import { capitalizeFirstLetter } from './utils';

const leading = 'leading-[2.7rem]';

export function PhraseWrapper({ children }) {
  return <div className={`text-3xl ${leading} [&>*]:inline-block mb-12`}>{children}</div>;
}

export function Selection({ children }) {
  return <div className="font-hbold mx-2 border-b-2 border-solid border-slate-700">{children}</div>;
}

export function Tooltip({ children, text }) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackAxisSideDirection: 'start'
      }),
      shift()
    ]
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  return (
    <>
      <span ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </span>
      <FloatingPortal>
        {isOpen && (
          <div
            style={{
              width: 'max-content',
              backgroundColor: '#444',
              color: 'white',
              fontSize: '12px',
              padding: '4px 8px',
              borderRadius: '4px',
              zIndex: 100000,
              ...floatingStyles
            }}
            ref={refs.setFloating}
            {...getFloatingProps()}
          >
            {text}
          </div>
        )}
      </FloatingPortal>
    </>
  );
}

export function TextWithOptions({
  label,
  options,
  selected,
  children,
  // disablePadding = false,
  closeOnSelect = true,
  padding,
  maxHeight,
  width,
  empty,
  defaultOpened,
  dropdownTitle,
  underline = true
}) {
  const disablePadding = false;
  const underlineClass = underline ? 'underline' : null;

  return (
    <div className="inline-block">
      <Popover
        disablePadding={disablePadding}
        padding={padding}
        maxHeight={maxHeight}
        width={width}
        defaultOpened={defaultOpened}
        dropdownTitle={dropdownTitle}
        buttonComponent={
          <div
            className={`lowercase font-hbold ${underlineClass} ${
              selected ? 'text-slate-700' : 'text-blue-600'
            }`}
            type="button"
          >
            {label}
          </div>
        }
      >
        {({ setIsOpen }) =>
          children ? (
            children({ setIsOpen })
          ) : options.length <= 0 ? (
            <Empty {...empty} />
          ) : (
            options.map(option => (
              <DropdownItem
                {...option}
                key={JSON.stringify(option)}
                onClick={() => {
                  if (closeOnSelect) {
                    setIsOpen(false);
                  }
                  option.onClick();
                }}
              />
            ))
          )
        }
      </Popover>
    </div>
  );
}

export function Popover({
  disablePadding = false,
  padding,
  buttonComponent,
  children,
  maxHeight = 'max-h-[300px]',
  width = 'w-[400px]',
  defaultOpened = false,
  dropdownTitle,
  customPlacement
}) {
  const [isOpen, setIsOpen] = useState(defaultOpened);

  const arrowRef = useRef(null);

  useEffect(() => {
    if (defaultOpened) {
      setTimeout(() => setIsOpen(true), 300);
    }
  }, [defaultOpened]);

  const {
    refs,
    floatingStyles,
    placement,
    context,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} }
  } = useFloating({
    open: isOpen,
    strategy: 'fixed',
    onOpenChange: setIsOpen,
    placement: customPlacement,
    middleware: [
      offset(10),
      flip({ fallbackAxisSideDirection: 'end' }),
      shift(),
      arrow({
        element: arrowRef
      })
    ],
    whileElementsMounted: autoUpdate
  });

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right'
  }[placement.split('-')[0]];

  const arrowBorders =
    staticSide === 'top'
      ? 'border-b-0 border-r-0'
      : staticSide === 'bottom'
        ? 'border-t-0 border-l-0'
        : staticSide === 'left'
          ? 'border-r-0 border-t-0'
          : 'border-l-0 border-b-0';

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const headingId = useId();
  const dropdownPadding = disablePadding ? 'nopadding' : padding || 'p-4 py-3 pb-0';
  const dropdownHeight = `${maxHeight} overflow-hidden overflow-y-scroll`;

  return (
    <>
      <button
        type="button"
        className={`${leading} inline-flex`}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {buttonComponent}
      </button>
      {isOpen && (
        <FloatingFocusManager context={context} modal={false}>
          <div
            className={`Popover dropdown rounded-lg bg-white  shadow-lg ring-1 ring-black ring-opacity-5 z-50`}
            ref={refs.setFloating}
            style={floatingStyles}
            aria-labelledby={headingId}
            {...getFloatingProps()}
          >
            <div
              ref={arrowRef}
              className={`dropdown__arrow reactArrow border-slate-200 border-solid border ${arrowBorders}`}
              style={{
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                [staticSide]: '-5px'
              }}
            />
            {dropdownTitle && (
              <h3
                className={`sticky top-0 text-base font-hmedium bg-white ${dropdownPadding} pt-1 pb-1 pl-6 border-b border-solid border-slate-200 rounded-t-lg`}
              >
                {dropdownTitle}
              </h3>
            )}
            <div className={`${dropdownPadding} ${dropdownHeight} ${width} flex flex-col rounded`}>
              {children({ setIsOpen })}
            </div>
          </div>
        </FloatingFocusManager>
      )}
    </>
  );
}

export function DropdownItem({ id, label, onClick, selected, disabled }) {
  return (
    <button
      key={id}
      type="button"
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={`text-sm font-hmedium px-2 px-2 rounded-md py-1 ${
        selected ? 'bg-blue-50 text-blue-600' : 'hover:bg-slate-100'
      } ${disabled && 'opacity-50 pointer-events-none'}`}
    >
      {capitalizeFirstLetter(label)}
    </button>
  );
}

export function BadgeButton({ label, selected, onClick, removable = false }) {
  const className = selected
    ? 'border-blue-600 text-blue-600'
    : 'border-slate-400 hover:border-slate-800 text-slate-600 hover:text-slate-800';

  return (
    <span
      className={`${className} ${leading} max-w-full inline-block font-hbold underline lowercase cursor-pointer`}
      onClick={onClick}
    >
      {label}
    </span>
  );
}

export function StartPhrase({ className, children }) {
  return <div className={`${className} truncate inline flex-shrink-0`}>{children}</div>;
}

export function Paragraph({ children }) {
  return <div className="text-lg font-hmedium mb-6 flex-wrap">{children}</div>;
}

export function Empty({ phrase, ctaProps }) {
  return (
    <div className="min-w-[200px] flex flex-col items-center justify-center p-4 border-dashed border-slate-300 border rounded-lg">
      <div className="text-sm text-slate-600">{phrase}</div>
      {ctaProps && (
        <a
          {...ctaProps}
          className="mt-2 text-xs font-hmedium text-white bg-blue-600 px-2 py-1 rounded-full"
        >
          {ctaProps.label}
        </a>
      )}
    </div>
  );
}

export function Loading({ phrase }) {
  return (
    <div className="min-w-[200px] flex flex-col items-center justify-center p-4 border-dashed border-slate-300 border rounded-lg">
      <div className="flex flex-col items-center space-y-4">
        <Spinner classes="h-7 w-7 text-blue-600" />
        <div className="text-sm text-slate-600">{phrase}</div>
      </div>
    </div>
  );
}

export function TrashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className="w-5 h-5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );
}

export function Spinner({ classes }) {
  return (
    <svg
      className={`animate-spin ${classes}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
}

export function SubmitButton({ submittable, isLoading, onSubmit }) {
  return (
    <div className="sticky bottom-0 bg-white/50 pb-4">
      <button
        disabled={!submittable || isLoading}
        className="flex items-center space-x-2 font-hmedium bg-blue-600 text-white rounded-full p-2 px-4 disabled:opacity-50 disabled:pointer-events-none"
        onClick={onSubmit}
      >
        {isLoading ? (
          <Spinner classes="-ml-1 mr-3 h-5 w-5 text-white" />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
            />
          </svg>
        )}

        <span>Enregistrer</span>
      </button>
    </div>
  );
}

export function ListButton({ label, onClick }) {
  return (
    <button
      className="max-w-full group text-2xl no-underline text-slate-700 flex items-center space-x-2 rounded-lg -translate-x-2"
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
    >
      <div className="max-w-full inline-flex items-center space-x-2 px-2 group-hover:bg-slate-50 px-2 rounded-lg">
        <span className="flex-shrink-0">-</span>
        <span className="truncate">{label}</span>
        <span className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-sm">
          <TrashIcon />
        </span>
      </div>
    </button>
  );
}

export const ClearIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      className="w-6 h-6"
    >
      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
};

export const CaretdownIcon = ({ classes = 'w-6 h-6' }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={classes}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
};

export default Popover;
